import { format, isToday, isTomorrow, isThisWeek, isWeekend, parseISO, isSameDay } from "date-fns";
import { sv } from "date-fns/locale";
import { distanceKm, formatDistance } from "@/lib/map/distance";
import type { OutingTag } from "@/types/outings";

/**
 * Format an outing's date range to a human-friendly Swedish label.
 */
export function formatOutingDateLabel(startDate: string, endDate: string): string {
  const start = parseISO(startDate);
  const end = parseISO(endDate);

  const startTime = format(start, "HH:mm");
  const endTime = format(end, "HH:mm");
  const hasDistinctEnd = !isSameDay(start, end) || endTime !== startTime;
  const startIsZero = startTime === "00:00";

  /** Build time suffix: "· 16:00" or "· 16:00–18:00" */
  function timeSuffix(): string {
    if (startIsZero) return ""; // midnight = no explicit time
    if (!hasDistinctEnd) return ` · ${startTime}`;
    if (isSameDay(start, end)) return ` · ${startTime}–${endTime}`;
    return ` · ${startTime}`;
  }

  if (isToday(start)) {
    return `Idag${timeSuffix()}`;
  }
  if (isTomorrow(start)) {
    return `Imorgon${timeSuffix()}`;
  }

  // Check if it falls on a weekend this week
  const startDay = start.getDay();
  if (isThisWeek(start, { locale: sv }) && (startDay === 6 || startDay === 0 || startDay === 5)) {
    if (isSameDay(start, end)) {
      return `${format(start, "EEE", { locale: sv })}${timeSuffix()}`;
    }
    return "I helgen";
  }

  if (isSameDay(start, end)) {
    return `${format(start, "d MMM", { locale: sv })}${timeSuffix()}`;
  }

  return `${format(start, "d MMM", { locale: sv })} – ${format(end, "d MMM", { locale: sv })}`;
}

/**
 * Compute a distance label like "6 km bort".
 */
export function computeDistanceLabel(
  userLat: number | null,
  userLng: number | null,
  outingLat: number | null,
  outingLng: number | null
): string | null {
  if (userLat == null || userLng == null || outingLat == null || outingLng == null) {
    return null;
  }
  const km = distanceKm(userLat, userLng, outingLat, outingLng);
  return `${formatDistance(km)} bort`;
}

/**
 * Pick the most important badge tag for display (max 1).
 * Priority: barnvänligt > plocka-själv > kultur > gratis
 */
const TAG_PRIORITY: OutingTag[] = ['barnvänligt', 'plocka-själv', 'kultur', 'gratis'];

export function pickPrimaryBadge(tags: OutingTag[]): OutingTag | null {
  for (const t of TAG_PRIORITY) {
    if (tags.includes(t)) return t;
  }
  return null;
}

/** Swedish label for a tag */
export const TAG_LABELS: Record<OutingTag, string> = {
  'barnvänligt': 'Barnvänlig',
  'plocka-själv': 'Plocka själv',
  'kultur': 'Kultur',
  'gratis': 'Gratis',
};

/** Derive outing tags from event data (MVP heuristic) */
export function deriveOutingTags(
  title: string,
  description: string | null,
  isFree: boolean
): OutingTag[] {
  const tags: OutingTag[] = [];
  const text = `${title} ${description ?? ''}`.toLowerCase();

  if (text.includes('barn') || text.includes('familj')) tags.push('barnvänligt');
  if (text.includes('plocka') || text.includes('självplock')) tags.push('plocka-själv');
  if (text.includes('kultur') || text.includes('historia') || text.includes('fäbod')) tags.push('kultur');
  if (isFree) tags.push('gratis');

  return tags;
}
